'use client';

import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { Fragment, useMemo, type ReactNode } from 'react';
import type { Currency } from 'utils/Currency';
import { CurrencySymbol, useFormatPrice } from 'hooks/pricing/useFormattedPrice';
import { useBuyContext } from '../../state/BuyContext';
import {
  cardHasOptions,
  resolve,
  type CardConfig,
  type ResolveCtx,
  type SelectionsMap,
} from '../../state/cardSchema';
import { useBuyTotals } from '../../state/useBuyTotals';
import { CartLineItem } from '../CartLineItem';
import { CheckoutButton } from './CheckoutButton';
import styles from './CartSummaryCard.module.css';

type DerivedLine = { id: string; label: ReactNode; price: number };

const walkLines = (
  cards: CardConfig[],
  selections: SelectionsMap,
  ctx: ResolveCtx,
  out: DerivedLine[],
): void => {
  for (const card of cards) {
    if (card.kind === 'subsection') {
      walkLines([card.child], selections, ctx, out);
      continue;
    }
    if (card.kind === 'composite') {
      walkLines(card.children, selections, ctx, out);
      continue;
    }
    if (card.kind === 'actionCard') continue;
    if (!cardHasOptions(card)) continue;

    const selected = selections[card.id];
    if (selected == null) continue;
    const ids = Array.isArray(selected) ? selected : [selected];

    for (const optionId of ids) {
      const option = card.options.find((o) => o.id === optionId);
      if (!option) continue;
      if (card.kind === 'largePrompt' && (option as { isDecline?: boolean }).isDecline) continue;
      const maybePrice = (option as { price?: unknown }).price;
      const resolved = resolve(maybePrice as Parameters<typeof resolve<number>>[0], ctx);
      if (typeof resolved !== 'number') continue;
      let label: ReactNode = card.title ?? card.id;
      if ('label' in option && option.label) label = option.label;
      else if ('title' in option && option.title) label = option.title;
      out.push({ id: `${card.id}:${option.id}`, label, price: resolved });
    }
  }
};

export type CartSummaryCardProps = {
  heading?: ReactNode;
  shippingNote?: ReactNode;
  benefits?: { icon: ReactNode; label: ReactNode }[];
  isEnabled?: () => boolean;
  onDisabledClick?: () => void;
  ctaLabel?: ReactNode;
};

export const CartSummaryCard = ({
  heading,
  shippingNote = '*Shipping fees and taxes will be calculated at checkout.',
  isEnabled,
  onDisabledClick,
  ctaLabel = 'Add to Cart',
}: CartSummaryCardProps) => {
  const {
    productName,
    currency,
    basePrice,
    cards,
    selections,
    resolveCtx,
    offersData,
  } = useBuyContext();
  const { total } = useBuyTotals();

  const symbol = CurrencySymbol[currency.toLowerCase() as Currency];

  const lines = useMemo(() => {
    const out: DerivedLine[] = [];
    walkLines(cards, selections, resolveCtx, out);
    return out;
  }, [cards, selections, resolveCtx]);
  const formatPriceWithComma = useFormatPrice();

  return (
    <div className={styles.card} id="order-summary-card" aria-labelledby="order-summary-heading">
      <h4 id="order-summary-heading">Here&apos;s your order summary</h4>

      <div className={styles.content}>
        <h5>{heading ?? productName}</h5>

        <CartLineItem
          label={productName}
          price={
            <>
              {symbol}
              {formatPriceWithComma(basePrice)}
            </>
          }
        />

        {lines.map((line) => (
          <div key={line.id} style={{ marginTop: 8 }}>
            <CartLineItem
              label={line.label}
              price={
                line.price === 0 ? (
                  'FREE'
                ) : (
                  <>
                    +{symbol}
                    {formatPriceWithComma(line.price)}
                  </>
                )
              }
            />
          </div>
        ))}
      </div>

      <div className={styles.divider} />

      <div className={styles.totalRow}>
        <span>Total</span>
        <span>
          {symbol}
          {formatPriceWithComma(total)}
        </span>
      </div>

      <div className={styles.divider} />

      {shippingNote ? <p className={styles.taxInfo}>{shippingNote}</p> : null}

      <CheckoutButton
        isEnabled={isEnabled}
        onDisabledClick={onDisabledClick}
        productLabel={productName}
      >
        {ctaLabel}
      </CheckoutButton>

      {offersData && offersData.length > 0 && (
        <div className={styles.paymentOffers}>
          {offersData.map((offer, index) => (
            <Fragment key={index}>
              {index > 0 && <div className={styles['offer-divider']} />}
              <div className={styles['offer-item']}>
                <div className={styles['offer-icon']}>
                  {offer.image?.[0] && (
                    <CustomImage
                      src={offer.image[0]}
                      alt=""
                      width="24"
                      height="24"
                    />
                  )}
                </div>
                <p>{offer.title}</p>
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
